// Lib
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { getUserEnableFeatureSuggestion } from '../../../common/users/utils/userPropertyUtils';
import { asObject } from '../../../common/utils/immutableHelper';
import {
    incrementFeatureSuggestionDismissCount,
    resetFeatureSuggestionDismissCount,
} from './featureSuggestionDismissalService';

// Actions
import { elementUnsuggestFeature } from './suggestionActions';
import { updateCurrentUser } from '../../user/currentUserActions';

// Hooks
import { useTranslator } from '../../components/i18n/useTranslator';
import { getCurrentUser } from '../../user/currentUserSelector';

// Components
import ElementTip from './ElementTip';
import { usePlaySound } from '../../sounds/usePlaySound';
import { SoundId } from '../../sounds/soundConstants';
import AnchorButton from '../../components/buttons/AnchorButton';

type AnchorClickEvent = Parameters<React.MouseEventHandler<HTMLAnchorElement>>[0];

const FEATURE_SUGGESTION_DISMISS_THRESHOLD = 3;
type FeatureSuggestionProps = {
    question: React.ReactNode;
    onConfirm: (event: AnchorClickEvent) => void;
    onDismiss: (event: AnchorClickEvent) => void;
    added?: boolean;
    icon?: object;
    className?: string;
    onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
    suggestion?: string;
    feature?: string;
    element?: ImMNElement;
    /** Sound to play on confirm (e.g. delete for "Delete completed tasks?"); confirm is silent without one */
    confirmSound?: SoundId;
};

const FeatureSuggestion: React.FC<FeatureSuggestionProps> = (props) => {
    const {
        question,
        onConfirm: onConfirmProp,
        onDismiss: onDismissProp,
        element,
        suggestion,
        feature,
        confirmSound,
    } = props;

    const dispatch = useDispatch();
    const currentUser = useSelector(getCurrentUser);
    const playSound = usePlaySound();

    const translate = useTranslator('elements');
    const yesLabel = translate('featureSuggestions.yes');
    const noThanksLabel = translate('featureSuggestions.noThanks');

    const handleUnsuggest = useCallback(() => {
        dispatch(elementUnsuggestFeature({ id: getElementId(element), feature: suggestion }));
    }, [element, suggestion]);

    const suggestionKey = feature || suggestion;

    const disableFeatureSuggestion = useCallback(
        (featureKey?: string) => {
            if (!featureKey || !currentUser) return;

            const existingEnableFeatureSuggestion =
                asObject<Record<string, number | null>>(getUserEnableFeatureSuggestion(currentUser)) || {};
            if (existingEnableFeatureSuggestion[featureKey] === null) return;

            dispatch(
                updateCurrentUser({
                    changes: {
                        settings: {
                            enableFeatureSuggestion: {
                                ...existingEnableFeatureSuggestion,
                                [featureKey]: null,
                            },
                        },
                    },
                }),
            );
        },
        [currentUser],
    );

    const onConfirm = useCallback(
        (event: AnchorClickEvent) => {
            if (suggestionKey) resetFeatureSuggestionDismissCount(suggestionKey);
            if (confirmSound) playSound(confirmSound);
            handleUnsuggest();
            onConfirmProp(event);
        },
        [onConfirmProp, suggestionKey, playSound, confirmSound],
    );

    const onDismiss = useCallback(
        (event: AnchorClickEvent) => {
            event.preventDefault();
            event.stopPropagation();

            if (
                suggestionKey &&
                incrementFeatureSuggestionDismissCount(suggestionKey) >= FEATURE_SUGGESTION_DISMISS_THRESHOLD
            ) {
                disableFeatureSuggestion(suggestionKey);
                resetFeatureSuggestionDismissCount(suggestionKey);
            }

            handleUnsuggest();

            onDismissProp(event);
        },
        [disableFeatureSuggestion, element, suggestionKey],
    );

    return (
        <ElementTip className="FeatureSuggestion" {...props}>
            <span className="question">{question}</span>
            <AnchorButton onClickFn={onConfirm}>{yesLabel}</AnchorButton>
            <AnchorButton onClickFn={onDismiss}>{noThanksLabel}</AnchorButton>
        </ElementTip>
    );
};

export default FeatureSuggestion;
